import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

import {
  allRecordsCql,
  CourseReserveDetailsData,
  CourseReserveSearchState,
  CourseReservesItemDto,
  ReserveListingsDto,
  SearchInstanceDto,
  SortField,
} from '@app/components';
import { usePaging } from '@app/hooks';
import { makeRequest } from '@app/utils';
import {
  defaultRecordsPerPage,
  ShapeWithTotalRecords,
  SortDirection,
} from '@app/constants';

type ReserveListings = ShapeWithTotalRecords<{
  reserves: ReserveListingsDto[];
}>;

interface CourseReserveDetailsDto {
  courses: CourseReservesItemDto[];
}

type ReserveListingsTransformed = ShapeWithTotalRecords<{
  reserves: CourseReserveDetailsData[];
}>;

type CourseReserveDetails = ShapeWithTotalRecords<{
  courseQuery: UseQueryResult<CourseReserveDetailsDto>;
  courseListingsQuery: UseQueryResult<ReserveListingsTransformed>;
  courseReserve: CourseReservesItemDto;
  pages: SearchInstanceDto[][];
  currentPage?: CourseReserveDetailsData[];
}>;

const sortFieldMap = {
  [SortField.title]: 'copiedItem.title',
  [SortField.author]: 'copiedItem.contributors',
  [SortField.publicationYear]: 'copiedItem.publicationYear',
};

const formatCourseListingsData = (
  data: ReserveListingsDto[] = []
): CourseReserveDetailsData[] =>
  data.map(
    ({
      copiedItem: {
        instanceId,
        title,
        contributors,
        publication,
        sourceTypes,
        isbns,
      },
    }) => ({
      id: instanceId,
      title,
      publication,
      contributors,
      sourceTypes,
      isbns,
    })
  );

export const useCourseReserveDetailsFetch = (
  state: CourseReserveSearchState
): CourseReserveDetails => {
  const { id } = useParams();
  const [courseReserve, setCourseReserve] = useState<CourseReservesItemDto>(
    null!
  );
  const [courseListingId, setCourseListingId] = useState('');
  const [pages, setPages] = useState<SearchInstanceDto[][]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const courseReserveDetailsQuery = useQuery(['courseReserveDetails', id], () =>
    makeRequest<CourseReserveDetailsDto>({
      url: '/opac-courses/courses',
      params: {
        query: `id==${id}`,
      },
    })
  );

  useEffect(() => {
    if (courseReserveDetailsQuery.data) {
      setCourseReserve(courseReserveDetailsQuery.data.courses[0]);
      setCourseListingId(
        courseReserveDetailsQuery.data.courses[0].courseListingId
      );
    }
  }, [courseReserveDetailsQuery.data]);

  const courseListingsQuery = useQuery<
    ReserveListings,
    unknown,
    ReserveListingsTransformed
  >(
    ['courseListings', courseListingId, state],
    () =>
      makeRequest<ReserveListings>({
        url: `/opac-courses/courselistings/${courseListingId}/reserves`,
        params: {
          query: `${allRecordsCql} sortBy ${
            sortFieldMap[state.sortField ?? SortField.title]
          }/sort.${state.sortDirection ?? SortDirection.asc}`,
          limit: defaultRecordsPerPage,
          ...(state.pageNumber > 1 && {
            offset: defaultRecordsPerPage * (state.pageNumber - 1),
          }),
        },
      }),
    {
      enabled: Boolean(courseListingId),
      cacheTime: 0,
      select: data => ({
        ...data,
        reserves: formatCourseListingsData(data.reserves),
      }),
    }
  );

  useEffect(() => {
    if (courseListingsQuery.data?.totalRecords !== undefined) {
      setTotalRecords(courseListingsQuery.data.totalRecords);
    }
  }, [courseListingsQuery.data?.totalRecords]);

  usePaging<CourseReserveDetailsData>({
    areRecordsFetched: courseListingsQuery.isSuccess,
    records: courseListingsQuery.data?.reserves,
    isNewPageRequest: state.pageNumber !== 1,
    pageNumber: state.pageNumber,
    setPages,
  });

  return {
    courseReserve,
    courseQuery: courseReserveDetailsQuery,
    courseListingsQuery,
    pages,
    totalRecords,
    currentPage: courseListingsQuery.data?.reserves,
  };
};
